import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
  useGetAllStatuses,
  useGetAllSystemDetailCodesDropdown,
} from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import debounce from "lodash.debounce";
import {
  useGetAllSearchVisitRequests,
  useGetAllVisitRequestTypes,
} from "../../hooks/visitor/query";
import useAuthStore from "../../store/Auth";
import { APP_ROUTES } from "../../routes";
import { useLocation } from "react-router-dom";
import {
  EXPORT_PAGE_SIZE,
  PAGE_NAME,
  SYSTEM_DETAIL_CODES,
} from "../../constants/common";
import i18n from "../../i18n";
import { Button } from "../button";
import { ExportApprovedVisitRequestsForExcelPDFBody } from "../../hooks/visitor/interface";

interface SearchVisitRequestCardProps {
  isVisitReqType?: boolean;
  setAllVisitorAppointments: Dispatch<SetStateAction<never[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportApprovedVisitRequestsForExcelPDFBody | undefined>
  >;
  pageSize: number;
}

const SearchVisitRequestCardSchema = Yup.object({
  name: Yup.string(),
  companyName: Yup.string(),
  number: Yup.string().matches(
    /^\d{1,14}$/,
    i18n.language === "en" ? "Enter a valid number." : "أدخل رقماً صحيحاً.",
  ),
  location: Yup.string(),
  building: Yup.string(),
  status: Yup.string(),
  visitReqType: Yup.string(),
  visitStartDate: Yup.string(),
  visitEndDate: Yup.string(),
  visitDurationType: Yup.string(),
});

const initialState = {
  name: "",
  companyName: "",
  number: "",
  location: "",
  building: "",
  status: "",
  visitReqType: "",
  visitStartDate: "",
  visitEndDate: "",
  visitDurationType: "",
};

const SearchVisitRequestCard = ({
  isVisitReqType = true,
  setAllVisitorAppointments,
  setIsLoading,
  page,
  setPages,
  search,
  setCount,
  setExportBody,
  pageSize,
}: SearchVisitRequestCardProps) => {
  const user = useAuthStore((state) => state.user);

  const {
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useFormWithSchema(SearchVisitRequestCardSchema, {
    defaultValues: {
      location: "",
      status: "2",
    },
  });

  const name = watch("name");
  const companyName = watch("companyName");
  const number = watch("number");

  const [debouncedName, setDebouncedName] = useState(name);
  const [debouncedCompanyName, setDebouncedCompanyName] = useState(companyName);
  const [debouncedNumber, setDebouncedNumber] = useState(number);

  const location = watch("location");
  const building = watch("building");
  const status = watch("status");
  const visitReqType = watch("visitReqType");
  const fromDate = watch("visitStartDate");
  const toDate = watch("visitEndDate");
  const visitDurationType = watch("visitDurationType");

  useEffect(() => {
    const handler = debounce((value) => setDebouncedName(value), 500);
    handler(name);
    return () => handler.cancel();
  }, [name]);

  useEffect(() => {
    const handler = debounce((value) => setDebouncedCompanyName(value), 500);
    handler(companyName);
    return () => handler.cancel();
  }, [companyName]);

  useEffect(() => {
    const handler = debounce((value) => setDebouncedNumber(value), 500);
    handler(number);
    return () => handler.cancel();
  }, [number]);

  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const params = useLocation();

  const { data: allStatuses } = useGetAllStatuses();
  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: visitRequestTypes } = useGetAllVisitRequestTypes();
  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: location ? location.toString() : "",
  });

  const { data: allVisitDurations } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.VISIT_DURATION_TYPES,
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings,
          )
        : [],
    [allBuildings],
  );

  const visitDurations = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allVisitDurations),
    [allVisitDurations],
  );

  const { data: visitRequests, isPending: isVisitRequestsLoading } =
    useGetAllSearchVisitRequests({
      S_FullName: debouncedName || "",
      S_Sponsor: debouncedCompanyName || "",
      S_MobileNo: debouncedNumber || "",
      N_LocationId: Number(location) || 0,
      n_BuildingID: building ? Number(building) : 0,
      N_ApprovalStatus: Number(status),
      ...(fromDate && { FromDate: fromDate }),
      ...(toDate && { ToDate: toDate }),
      N_RoleId:
        params.pathname === APP_ROUTES.APPROVE_VISIT_REQUEST
          ? user?.n_RoleID
          : 0,
      N_ExternalAppointment:
        params.pathname === APP_ROUTES.APPROVE_VISIT_REQUEST ? 2 : 0,
      N_VisitRequestTypeId: Number(visitReqType) || 0,
      VisitDurationType: Number(visitDurationType) || 0,

      N_PageSize: pageSize,
      N_PageNumber: page,
      S_PageName:
        params.pathname === APP_ROUTES.APPROVE_VISIT_REQUEST
          ? PAGE_NAME.APPROVE_APPOINTMENT_BY_STAFF
          : PAGE_NAME.SEARCH_APPOINTMENT_BY_STAFF,
      S_Search: search,
    });

  useEffect(() => {
    setIsLoading?.(isVisitRequestsLoading);
  }, [isVisitRequestsLoading, setIsLoading]);

  useEffect(() => {
    setExportBody({
      S_FullName: debouncedName || "",
      S_Sponsor: debouncedCompanyName || "",
      S_MobileNo: debouncedNumber || "",
      N_LocationId: Number(location) || 0,
      n_BuildingID: building ? Number(building) : 0,
      N_ApprovalStatus: Number(status),
      ...(fromDate && { FromDate: fromDate }),
      ...(toDate && { ToDate: toDate }),
      N_RoleId:
        params.pathname === APP_ROUTES.APPROVE_VISIT_REQUEST
          ? user?.n_RoleID
          : 0,
      N_ExternalAppointment:
        params.pathname === APP_ROUTES.APPROVE_VISIT_REQUEST ? 2 : 0,
      N_VisitRequestTypeId: Number(visitReqType) || 0,
      VisitDurationType: Number(visitDurationType) || 0,

      N_PageSize: EXPORT_PAGE_SIZE,
      N_PageNumber: page,
      S_PageName:
        params.pathname === APP_ROUTES.APPROVE_VISIT_REQUEST
          ? PAGE_NAME.APPROVE_APPOINTMENT_BY_STAFF
          : PAGE_NAME.SEARCH_APPOINTMENT_BY_STAFF,
      S_Search: search,
    });
  }, [
    building,
    debouncedCompanyName,
    debouncedName,
    debouncedNumber,
    fromDate,
    location,
    page,
    pageSize,
    params.pathname,
    search,
    setExportBody,
    status,
    t,
    toDate,
    user?.n_RoleID,
    visitDurationType,
    visitReqType,
    visitRequests,
  ]);

  useEffect(() => {
    if (visitRequests) {
      setAllVisitorAppointments(visitRequests.data || []);
      setPages(visitRequests.pagination?.pages || 1);
      setCount(visitRequests.pagination?.count || 0);
    }
  }, [
    params.pathname,
    setCount,
    setAllVisitorAppointments,
    setPages,
    visitRequests,
  ]);

  useEffect(() => {
    if (search && status === "2") {
      setValue("status", "0");
    }
  }, [search, setValue, status]);

  const visitReqTypes = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", visitRequestTypes),
    [visitRequestTypes],
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations,
      ),
    [allLocations],
  );

  const statuses = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allStatuses),
    [allStatuses],
  );

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <Controller
                name="name"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("visitorName")}
                    onChange={(value) => {
                      onChange(value);
                      status === "2" && setValue("status", "0");
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />
              <Controller
                name="companyName"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("companyName")}
                    onChange={(value) => {
                      onChange(value);
                      status === "2" && setValue("status", "0");
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />
              <Controller
                name="number"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <div className="flex flex-col">
                    <TextInput
                      label={t("phoneNumber")}
                      onChange={(value) => {
                        onChange(value);
                        status === "2" && setValue("status", "0");
                      }}
                      error={errors?.[name]?.message}
                      value={value}
                      isInfoToolTip
                      infoToolTip={<p>{t("enterPhoneNumber")}</p>}
                    />
                  </div>
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      status === "2" && setValue("status", "0");
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <div>
                <Controller
                  name="visitStartDate"
                  control={control}
                  render={({ field: { name, value, onChange } }) => (
                    <TextInput
                      type="date"
                      label={t("visitStartDate")}
                      value={value}
                      onChange={(e) => {
                        onChange(e.target.value);
                        status === "2" && setValue("status", "0");
                      }}
                    />
                  )}
                />
              </div>

              <Controller
                name="visitEndDate"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("visitEndDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                      status === "2" && setValue("status", "0");
                    }}
                  />
                )}
              />
              {isVisitReqType && (
                <Controller
                  name="visitReqType"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <ComboboxInput
                      options={visitReqTypes}
                      value={
                        visitReqTypes?.find((item) => item.value === value)
                          ?.labelEn || ""
                      }
                      onChange={(value) => {
                        onChange(value);
                        status === "2" && setValue("status", "0");
                      }}
                      error={errors?.[name]?.message}
                      label={t("visitorType")}
                    />
                  )}
                />
              )}

              <Controller
                name="status"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={statuses}
                    onChange={onChange}
                    label={t("status")}
                    value={
                      statuses?.find((item) => item.value === value)?.labelEn ||
                      ""
                    }
                  />
                )}
              />

              <Controller
                name="visitDurationType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    label={t("visitDurationType")}
                    options={visitDurations}
                    value={
                      visitDurations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default memo(SearchVisitRequestCard);
